import { RedirectToResult, Transition } from '@uirouter/angular';
import { refreshPropertyAddressAsync } from '../shared/helpers/address.helper';
import { AccountTypes } from '../shared/models/deal-resources/borrower-asset.model';
import { ClientJourneyContext } from '../shared/services/deal/client-journey-context';
import { ResourceContextStore } from '../shared/services/resource-context-store';
import { ClientJourneyStateNames, JourneyStates } from './journey-states';
import { SlidesStateNames } from './slides-states';

export function defaultStateRedirectHandler(trans: Transition) {
  const ignoredStateNames = [
    ClientJourneyStateNames.Login,
    ClientJourneyStateNames.Logout,
    ClientJourneyStateNames.ContextLoader,
    ClientJourneyStateNames.Error,
    ClientJourneyStateNames.LoggedOutError,
  ];

  const toStateName = trans.to()?.name;
  if (!toStateName) return null;
  if (ignoredStateNames.includes(toStateName)) return null;

  const shortCode = trans.params()?.shortCode;
  const hasShortCode = !!shortCode;

  // If short code is missing redirect to error state
  if (!hasShortCode) {
    trans.abort();
    JourneyStates.transitionToErrorState(trans.router);
    return null;
  }

  // Assume everything's good, and test to see
  // if a redirect is necessary.
  let redirectToContextLoader = false;

  const loanCode = trans.params()?.loanCode;
  const dealKey = trans.params()?.dealKey;
  const hasLoanCode = !!loanCode;
  const hasDelKey = !!dealKey;

  const clientJourneyContext = trans.injector().get<ClientJourneyContext>(ClientJourneyContext);

  const hasAgent = !!clientJourneyContext.agent;
  const shortCodeMatchesAgent = hasAgent && clientJourneyContext.agent.shortCode == shortCode;
  const isAgentLoaded = hasAgent && shortCodeMatchesAgent;

  // A missing or invalid agent requires a redirect
  redirectToContextLoader ||= !isAgentLoaded;

  if (hasLoanCode || hasDelKey) {
    const hasDeal = !!clientJourneyContext.deal;
    const loanCodeMatchesDeal = hasDeal && clientJourneyContext.deal.loanCode == loanCode;
    const dealKeyMatchesDeal = hasDeal && clientJourneyContext.deal.dealKey == dealKey;
    const isDealLoaded = hasDeal && loanCodeMatchesDeal && dealKeyMatchesDeal;

    // A missing or invalid deal requires a redirect
    redirectToContextLoader ||= !isDealLoaded;
  }

  // We're done, everything's loaded
  if (!redirectToContextLoader) return;

  const toState = trans.to();
  const toParams = trans.params('to');

  let returnUrl = '';
  if (toState) {
    returnUrl = trans.router.stateService.href(toState, toParams);
  }

  if (!returnUrl) {
    returnUrl = trans.router.stateService.href(ClientJourneyStateNames.Default, toParams);
  }

  // The stateService seems to resolve the URL with the baseHref as part
  // of the URL. This is a problem since navigating to a route that includes
  // the baseHref will cause the baseHref to be duplicated. This will strip
  // the baseHref from the returnUrl.
  const baseHref = trans.router.locationConfig.baseHref();
  if (baseHref && returnUrl.startsWith(baseHref)) {
    returnUrl = returnUrl.substring(baseHref.length - 1);
  }

  const contextLoaderParams = {
    returnUrl,
    shortCode: toParams?.shortCode,
    loanCode: toParams?.loanCode,
    dealKey: toParams?.dealKey,
    ceContactKey: toParams?.ceContactKey,
  };
  trans.abort();
  trans.router.stateService.go(ClientJourneyStateNames.ContextLoader, contextLoaderParams, {
    location: false,
  });
}

export function borrowersRedirectHandler(transition: Transition): RedirectToResult {
  const clientJourneyContext = transition.injector().get<ClientJourneyContext>(ClientJourneyContext);

  const hasReachedBorrowerSummary = clientJourneyContext.engagementService.hasReachedSate(
    SlidesStateNames.BorrowersSummary,
  );

  // Once a user has made it to the summary once, we always return them
  // to the summary. If the user has never reached the summary, we start
  // them at the edit page.
  const redirectState = hasReachedBorrowerSummary ? SlidesStateNames.BorrowersSummary : SlidesStateNames.BorrowersEdit;

  // If the redirect is unnecessary, return undefined
  const targetState = transition.targetState();
  if (redirectState == targetState.name()) return;

  return redirectState;
}

export function incomeRedirectHandler(transition: Transition): RedirectToResult {
  const clientJourneyContext = transition.injector().get<ClientJourneyContext>(ClientJourneyContext);

  const hasOtherIncome = clientJourneyContext.deal.borrowers.some(b => b.otherIncomes?.length);
  const hasEmployments = clientJourneyContext.deal.borrowers.some(b => b.employments?.length);

  // Resolve and return the redirect state
  const redirectState = hasOtherIncome || hasEmployments ? SlidesStateNames.IncomeSummary : SlidesStateNames.IncomeEdit;

  // If the redirect is unnecessary, return undefined
  const targetState = transition.targetState();
  if (redirectState == targetState.name()) return;

  return redirectState;
}

export function bankingRedirectHandler(transition: Transition): RedirectToResult {
  const clientJourneyContext = transition.injector().get<ClientJourneyContext>(ClientJourneyContext);

  const hasBankScrape = clientJourneyContext.deal.borrowers.some(b => b.bankScrapes.length);

  const hasAccountAsset = clientJourneyContext.deal.borrowers.some(b =>
    b.assets.some(a => AccountTypes.includes(a.type)),
  );

  // Resolve and return the redirect state
  const redirectState =
    hasBankScrape || hasAccountAsset ? SlidesStateNames.BankingSummary : SlidesStateNames.BankingEdit;

  // If the redirect is unnecessary, return undefined
  const targetState = transition.targetState();
  if (redirectState == targetState.name()) return;

  return redirectState;
}

export function assetsRedirectHandler(transition: Transition): RedirectToResult {
  const clientJourneyContext = transition.injector().get<ClientJourneyContext>(ClientJourneyContext);

  const hasAssets = clientJourneyContext.deal.borrowers.some(b => b.assets.some(a => AccountTypes.includes(a.type)));

  // Resolve and return the redirect state
  const redirectState = hasAssets ? SlidesStateNames.AssetsSummary : SlidesStateNames.AssetsEdit;

  // If the redirect is unnecessary, return undefined
  const targetState = transition.targetState();
  if (redirectState == targetState.name()) return;

  return redirectState;
}

export async function propertiesRedirectHandler(transition: Transition): Promise<RedirectToResult> {
  const clientJourneyContext = transition.injector().get<ClientJourneyContext>(ClientJourneyContext);

  const resourceContextStore = transition.injector().get<ResourceContextStore>(ResourceContextStore);

  await refreshPropertyAddressAsync(clientJourneyContext, resourceContextStore);

  const hasProperty = clientJourneyContext.deal.borrowers.some(b => b.properties?.length);

  // Resolve and return the redirect state
  const redirectState = hasProperty ? SlidesStateNames.PropertiesSummary : SlidesStateNames.PropertiesEdit;

  // If the redirect is unnecessary, return undefined
  const targetState = transition.targetState();
  if (redirectState == targetState.name()) return;

  return redirectState;
}

export function referralRedirectHandler(transition: Transition): RedirectToResult {
  const clientJourneyContext = transition.injector().get<ClientJourneyContext>(ClientJourneyContext);

  if (!clientJourneyContext.agent.referralActive) {
    return SlidesStateNames.Review;
  }

  const referralContact = clientJourneyContext.deal.referral?.contact;

  const referralName = `${referralContact?.firstName || ''} ${referralContact?.lastName || ''}`.trim();
  const referralCompany = referralContact?.companyName || '';
  const referralEmail = referralContact?.email || '';

  const hasReferral = referralName || referralCompany || referralEmail;

  // Resolve and return the redirect state
  const redirectState = hasReferral ? SlidesStateNames.Review : SlidesStateNames.ReferralEdit;

  // If the redirect is unnecessary, return undefined
  const targetState = transition.targetState();
  if (redirectState == targetState.name()) return;

  return redirectState;
}
